import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Observable } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics.service';
import {
  AutoScheduleNudgeState,
  DEFAULT_NUDGE_CONFIG,
  HOLDOUT_PERCENT,
  NudgeArm,
  NudgeConfig,
  NudgeEvent,
  assignArm,
  dismiss,
  evaluate,
  initialNudgeState,
  recordAdjustment,
} from '../dashboard-widgets/climate-control/auto-schedule-nudge/auto-schedule-nudge.logic';

export const AUTO_SCHEDULE_NUDGE_KEY = 'autoScheduleNudge.v1';

/**
 * Client-only owner of the "Adjusting a lot?" Auto Schedule nudge. Wraps the pure
 * eligibility engine with on-device persistence, an analytics side-channel, and a
 * visibility stream the banner subscribes to. `providedIn: 'root'` so the Climate
 * card and the banner share one instance.
 */
@Injectable({ providedIn: 'root' })
export class AutoScheduleNudgeService {
  private readonly config: NudgeConfig = DEFAULT_NUDGE_CONFIG;
  private readonly visible$ = new BehaviorSubject<boolean>(false);
  readonly nudge$: Observable<boolean> = this.visible$.asObservable();

  /**
   * Serializes every mutating operation (load -> compute -> save -> emit) so
   * overlapping calls (e.g. an MQTT-driven `refresh()` racing a
   * `recordManualAdjustment()`) never interleave their reads/writes and clobber
   * each other's state. Each public method enqueues its critical section here
   * and returns a promise that resolves once THAT section has finished.
   */
  private queue: Promise<void> = Promise.resolve();

  /**
   * Household identifier (the MQTT unit id) attached to every emitted event, so
   * downstream can count distinct *households* rather than distinct users (a unit
   * with multiple app users over-counts on userId alone). Supplied by the Climate
   * card through record/refresh and reused for the dismiss/set-up events.
   */
  private householdId?: string;

  /**
   * Id of the impression currently on screen (its start epoch-ms), cached so
   * `set_up_tapped` — fired from the banner, which never sees a reducer result —
   * can be correlated to the same impression as its `shown`.
   */
  private activeImpressionId: number | null = null;

  /**
   * Experiment arm for the current household. `control` households are eligible
   * and emit the same lifecycle events (tagged `arm`), but the banner is never
   * rendered — enabling a nudged-vs-control lift comparison. Derived
   * deterministically from `householdId`; defaults to `treatment` until known.
   */
  private currentArm: NudgeArm = 'treatment';

  /** Control-holdout percentage applied to this household. Defaults to the module
   *  constant (currently 0 = holdout disabled); overridable for tests / future
   *  remote config. */
  private holdoutPercent = HOLDOUT_PERCENT;

  constructor(private analytics: AnalyticsService) {}

  /** Record one committed manual temperature change, then re-evaluate visibility. */
  async recordManualAdjustment(hasAnyAutoSet: boolean, unitId?: string): Promise<void> {
    return this.enqueue(async () => {
      if (unitId !== undefined) this.householdId = unitId;
      this.currentArm = assignArm(this.householdId, this.holdoutPercent);
      const state = await this.load();
      const now = Date.now();
      const adjusted = recordAdjustment(state, now, this.config);
      const result = evaluate(adjusted, now, hasAnyAutoSet, this.config);
      await this.save(result.state);
      this.activeImpressionId = result.state.activeImpressionStartedAt;
      this.emit(result.events, result.state);
      // Control-arm households run the engine and emit events identically, but
      // the banner is never rendered.
      this.visible$.next(this.currentArm === 'control' ? false : result.visible);
    });
  }

  /** Re-evaluate visibility against the current clock and adoption state. */
  async refresh(hasAnyAutoSet: boolean, unitId?: string): Promise<void> {
    return this.enqueue(async () => {
      if (unitId !== undefined) this.householdId = unitId;
      this.currentArm = assignArm(this.householdId, this.holdoutPercent);
      const state = await this.load();
      const result = evaluate(state, Date.now(), hasAnyAutoSet, this.config);
      await this.save(result.state);
      this.activeImpressionId = result.state.activeImpressionStartedAt;
      this.emit(result.events, result.state);
      // Control-arm households run the engine and emit events identically, but
      // the banner is never rendered.
      this.visible$.next(this.currentArm === 'control' ? false : result.visible);
    });
  }

  /** Dismiss the active impression and open the cooldown window. */
  async dismissNudge(): Promise<void> {
    return this.enqueue(async () => {
      const state = await this.load();
      const result = dismiss(state, Date.now(), this.config);
      await this.save(result.state);
      this.emit(result.events, result.state);
      this.activeImpressionId = result.state.activeImpressionStartedAt;
      // Control-arm households run the engine and emit events identically, but
      // the banner is never rendered.
      this.visible$.next(this.currentArm === 'control' ? false : result.visible);
    });
  }

  /** Analytics only — the banner component performs navigation to Settings → Climate. */
  onSetUpTapped(): void {
    this.analytics.trackWidgetInteraction(
      'auto_schedule_nudge',
      'set_up_tapped',
      this.eventProps(this.activeImpressionId),
    );
  }

  /**
   * Chains `operation` onto the instance queue so it runs only after every
   * previously-enqueued operation has settled, guaranteeing a strictly
   * sequential load -> compute -> save -> emit per call. The returned promise
   * settles with `operation`'s own outcome; a failure never breaks the chain
   * for subsequently-enqueued operations.
   */
  private enqueue(operation: () => Promise<void>): Promise<void> {
    const run = this.queue.then(operation);
    // Keep the chain alive regardless of this op's outcome so a failure never
    // stalls subsequently-enqueued operations.
    this.queue = run.then(() => undefined, () => undefined);
    // Callers invoke this as `void ...`, so swallow (and log) failures here
    // rather than letting them surface as unhandled promise rejections. The
    // operation internals already handle their own recoverable errors; this is
    // the last-resort guard.
    return run.catch((err) => {
      console.error('[AutoScheduleNudge] operation failed', err);
    });
  }

  private emit(events: NudgeEvent[], state: AutoScheduleNudgeState): void {
    for (const event of events) {
      const props =
        event.type === 'shown'
          ? {
              ...this.eventProps(event.impressionId),
              lifetimeShownCount: state.lifetimeShownCount,
              // Distinguish a control-arm "would-have-shown" from a rendered banner.
              ...(this.currentArm === 'control' ? { withheld: true } : {}),
            }
          : this.eventProps(event.impressionId);
      this.analytics.trackWidgetInteraction('auto_schedule_nudge', event.type, props);
    }
  }

  /** Analytics props shared by every event: the impression id it belongs to and,
   *  when known, the household (unit) id. */
  private eventProps(impressionId: number | null): Record<string, any> {
    const props: Record<string, any> = { arm: this.currentArm };
    if (impressionId != null) props['impressionId'] = impressionId;
    if (this.householdId !== undefined) props['unitId'] = this.householdId;
    return props;
  }

  private async load(): Promise<AutoScheduleNudgeState> {
    try {
      const { value } = await Preferences.get({ key: AUTO_SCHEDULE_NUDGE_KEY });
      if (!value) return initialNudgeState();
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed !== 'object' || parsed === null) return initialNudgeState();
      return this.sanitize(parsed as Record<string, unknown>);
    } catch {
      return initialNudgeState();
    }
  }

  /**
   * Coerce a persisted blob into a well-typed state, replacing any field whose
   * type doesn't match with the initial default. A corrupt or legacy value (e.g.
   * `adjustments` that isn't an array) can then never reach — and crash — the
   * pure reducer's array/number operations.
   */
  private sanitize(raw: Record<string, unknown>): AutoScheduleNudgeState {
    const base = initialNudgeState();
    const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
    const adjustments = raw['adjustments'];
    const retired = raw['retiredByAdoption'];
    return {
      adjustments: Array.isArray(adjustments) ? adjustments.filter(isNum) : base.adjustments,
      lifetimeShownCount: isNum(raw['lifetimeShownCount']) ? raw['lifetimeShownCount'] : base.lifetimeShownCount,
      activeImpressionStartedAt: isNum(raw['activeImpressionStartedAt'])
        ? raw['activeImpressionStartedAt']
        : base.activeImpressionStartedAt,
      suppressedUntil: isNum(raw['suppressedUntil']) ? raw['suppressedUntil'] : base.suppressedUntil,
      retiredByAdoption: typeof retired === 'boolean' ? retired : base.retiredByAdoption,
    };
  }

  private async save(state: AutoScheduleNudgeState): Promise<void> {
    try {
      await Preferences.set({ key: AUTO_SCHEDULE_NUDGE_KEY, value: JSON.stringify(state) });
    } catch {
      // Persistence is best-effort; a failed write just means the next evaluation
      // re-reads the last good state. Never throw into the control flow.
    }
  }
}
